import React, { useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import DeleteModal from '../../../components/DeleteModal';
import { dispatchService } from '../../../services/central-api/dispatch';
import { Pagination } from '../../../components/Pagination';
import { ClientError } from '../../../services/central-api/base-service';
import MyDataTable from '../../../components/MyDataTable';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import CustomButton from '../../../components/CustomButton';
import FilterInput from '../../../components/Filter/components/FilterInput';
import Filter from './Filters';
import ObjectCategoryModal from './ObjectCategoryModal';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';

export default function ObjectCategories({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<DispatchParamList, 'ObjectsCategories'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DispatchParamList['ObjectsCategories']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedObjectCategory, setSelectedObjectCategory] = useState<ObjectCategory>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getPaginatedObjectsCategories', filters],
        queryFn: () => dispatchService.getPaginatedObjectsCategories(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    async function confirmDelete(objectCategory: BaseObjectCategory) {
        try {
            await dispatchService.deleteObjectCategory(objectCategory.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            setDeleteModalVisible(false);
            setSelectedObjectCategory(undefined);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleFormClose() {
        setSelectedObjectCategory(undefined);
        setModalVisible(false);
        refetch();
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return <Container>
        <Header
            title={translate('objectsCategories')}
            actions={
                <CustomButton
                    text={translate('register')}
                    onPress={() => {
                        setSelectedObjectCategory(undefined);
                        setModalVisible(true);
                    }}
                />
            }
        />
        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredObjectsCategories')}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter}
                />}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
            />


            <DataTableContainer
                dataTable={<MyDataTable
                    progressPending={isLoading}
                    columns={[
                        {
                            name: 'ID',
                            selector: row => row.id,
                            grow: 0
                        },
                        {
                            name: translate('name'),
                            selector: row => row.name,
                            grow: 5
                        },
                        {
                            name: translate('actingBody'),
                            selector: row => row.actingBody.name,
                            wrap: true,
                        },
                        {
                            cell: row => (
                                <DropdownButton
                                    actions={[
                                        {
                                            content: translate('editInformation'),
                                            onPress: () => {
                                                setSelectedObjectCategory(row);
                                                setModalVisible(true);
                                            }
                                        },
                                        {
                                            content: translate('delete'),
                                            variant: 'danger',
                                            onPress: () => {
                                                setSelectedObjectCategory(row);
                                                setDeleteModalVisible(true);
                                            }
                                        }
                                    ]}
                                />
                            ),
                            wrap: true,
                            button: true,
                        }
                    ]}
                    data={data?.rows ?? []}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count ?? 0}
                    pageSize={filters.limit}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}

                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                />}
            />
        </Card>

        <ObjectCategoryModal
            onClose={handleFormClose}
            objectCategoryId={selectedObjectCategory ? selectedObjectCategory.id : undefined}
            isModalVisible={isModalVisible}
            authenticatedUser={authenticatedUser}
        />



        {selectedObjectCategory &&
            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={selectedObjectCategory.name}
                onSubmit={async () => {
                    await confirmDelete(selectedObjectCategory);
                }}
                onCancel={() => {
                    setSelectedObjectCategory(undefined);
                }}
            />
        }

    </Container>;
}
